import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import {
  Bell,
  Cloud,
  Clock,
  FileImage,
  HardDrive,
  MessageSquare,
  Save,
  ShieldCheck,
  Users,
  ClipboardList,
  Smartphone,
  Mail,
  Pencil,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/api'
import jobsApi from '@/services/jobs'
import { useAuthStore } from '@/stores/auth-store'
import type { JobManagementConfiguration, JobManagementConfigurationUpdatePayload } from '@/types'

const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = value
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }

  const precision = size >= 10 || unitIndex === 0 ? 0 : 1
  return `${size.toFixed(precision)} ${units[unitIndex]}`
}


const bytesToMegabytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0'
  }

  return (value / (1024 * 1024)).toString()
}

type ConfigurationFormState = {
  track_worker: boolean
  push_notification: boolean
  sms_notification: boolean
  email_notification: boolean
  number_gallery_files: string
  maximum_file_size_mb: string
  work_hours_per_day: string
  per_day_worker_invoice_cutoff_time: string
  allow_worker_comments: boolean
  require_otp_for_completion: boolean
  auto_assign_primary_worker: boolean
  allow_expense_uploads: boolean
  allow_multiple_assignments: boolean
  enable_job_audit: boolean
  sender_email: string
  sms_gateway: string
  media_base_url: string
  gallery_storage_backend: 'local' | 's3'
  gallery_s3_bucket: string
  gallery_s3_region: string
  gallery_s3_access_key: string
  gallery_s3_secret_key: string
  clear_gallery_s3_secret_key: boolean
}

type FormErrors = Partial<Record<keyof ConfigurationFormState, string>>

const createFormState = (config: JobManagementConfiguration): ConfigurationFormState => ({
  track_worker: config.track_worker,
  push_notification: config.push_notification,
  sms_notification: config.sms_notification,
  email_notification: config.email_notification,
  number_gallery_files: String(config.number_gallery_files),
  maximum_file_size_mb: bytesToMegabytes(config.maximum_file_size),
  work_hours_per_day: String(config.work_hours_per_day),
  per_day_worker_invoice_cutoff_time: (config.per_day_worker_invoice_cutoff_time || '00:01').slice(0, 5),
  allow_worker_comments: config.allow_worker_comments,
  require_otp_for_completion: config.require_otp_for_completion,
  auto_assign_primary_worker: config.auto_assign_primary_worker,
  allow_expense_uploads: config.allow_expense_uploads,
  allow_multiple_assignments: config.allow_multiple_assignments,
  enable_job_audit: config.enable_job_audit,
  sender_email: config.sender_email || '',
  sms_gateway: config.sms_gateway || '',
  media_base_url: config.media_base_url || '',
  gallery_storage_backend: config.gallery_storage_backend || 'local',
  gallery_s3_bucket: config.gallery_s3_bucket || '',
  gallery_s3_region: config.gallery_s3_region || '',
  gallery_s3_access_key: config.gallery_s3_access_key || '',
  gallery_s3_secret_key: '',
  clear_gallery_s3_secret_key: false,
})

const notificationItems = [
  { key: 'push_notification', label: 'Push', icon: Bell },
  { key: 'sms_notification', label: 'SMS', icon: Smartphone },
  { key: 'email_notification', label: 'Email', icon: Mail },
] as const

const workflowItems = [
  { key: 'track_worker', label: 'Track worker location', icon: Users, capabilityKey: 'can_access_worker_tracking' },
  { key: 'allow_worker_comments', label: 'Worker comments', icon: MessageSquare },
  { key: 'require_otp_for_completion', label: 'OTP for completion', icon: ShieldCheck },
  { key: 'auto_assign_primary_worker', label: 'Auto assign primary worker', icon: Users },
  { key: 'allow_multiple_assignments', label: 'Multiple assignments', icon: ClipboardList },
  { key: 'enable_job_audit', label: 'Job audit log', icon: ClipboardList, capabilityKey: 'can_access_job_audit' },
] as const

const mediaItems = [
  { key: 'allow_expense_uploads', label: 'Expense uploads', icon: FileImage },
] as const

const fieldErrorClassName = 'border-destructive focus-visible:ring-destructive/30'

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const isValidUrl = (value: string) => {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function ToggleRow({
  label,
  checked,
  onCheckedChange,
  disabled = false,
  icon: Icon,
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/15 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background text-foreground">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <p className="truncate text-xs font-medium text-foreground">{label}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} className="scale-90" />
    </div>
  )
}

export default function JobConfigurationPage() {
  useLocation()
  const labels = useWorkspaceLabels()
  const queryClient = useQueryClient()
  const currentPost = useAuthStore((state) => state.currentPost)
  const capabilities = currentPost?.capabilities
  const { data, isLoading, error } = useQuery({
    queryKey: ['job-management-configuration'],
    queryFn: jobsApi.getManagementConfiguration,
  })
  const [formState, setFormState] = useState<ConfigurationFormState | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (data) {
      setFormState(createFormState(data))
      setErrors({})
    }
  }, [data])

  const updateMutation = useMutation({
    mutationFn: jobsApi.updateManagementConfiguration,
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(['job-management-configuration'], updatedConfig)
      setFormState(createFormState(updatedConfig))
      setIsEditing(false)
      toast.success(`${labels.entity} configuration updated`)
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const visibleWorkflowItems = workflowItems.filter((item) => (
    !('capabilityKey' in item)
    || capabilities?.[item.capabilityKey as keyof NonNullable<typeof capabilities>] !== false
  ))

  const setBooleanField = (key: keyof ConfigurationFormState, value: boolean) => {
    setErrors((current) => ({ ...current, [key]: undefined }))
    setFormState((current) => (current ? { ...current, [key]: value } : current))
  }

  const setTextField = (key: keyof ConfigurationFormState, value: string) => {
    setErrors((current) => ({ ...current, [key]: undefined }))
    setFormState((current) => (current ? { ...current, [key]: value } : current))
  }

  const handleReset = () => {
    if (data) {
      setFormState(createFormState(data))
      setErrors({})
    }
  }

  const handleStartEditing = () => {
    setIsEditing(true)
    setErrors({})
  }

  const handleCancelEditing = () => {
    handleReset()
    setIsEditing(false)
  }

  const validateForm = (currentState: ConfigurationFormState): FormErrors => {
    const nextErrors: FormErrors = {}
    const galleryFileCount = Number.parseInt(currentState.number_gallery_files, 10)
    const maximumFileSizeMb = Number.parseFloat(currentState.maximum_file_size_mb)
    const workHoursPerDay = Number.parseFloat(currentState.work_hours_per_day)

    if (!Number.isFinite(galleryFileCount) || galleryFileCount < 1) {
      nextErrors.number_gallery_files = 'Files per job must be at least 1.'
    }

    if (!Number.isFinite(maximumFileSizeMb) || maximumFileSizeMb <= 0) {
      nextErrors.maximum_file_size_mb = 'Maximum file size must be greater than 0 MB.'
    }

    if (!Number.isFinite(workHoursPerDay) || workHoursPerDay <= 0) {
      nextErrors.work_hours_per_day = 'Hours per day must be greater than 0.'
    }

    if (!/^\d{2}:\d{2}$/.test(currentState.per_day_worker_invoice_cutoff_time)) {
      nextErrors.per_day_worker_invoice_cutoff_time = 'Enter a valid cutoff time.'
    }

    if (currentState.sender_email.trim() && !isValidEmail(currentState.sender_email.trim())) {
      nextErrors.sender_email = 'Enter a valid sender email address.'
    }

    if (currentState.media_base_url.trim() && !isValidUrl(currentState.media_base_url.trim())) {
      nextErrors.media_base_url = 'Enter a valid media base URL starting with http or https.'
    }

    if (currentState.gallery_storage_backend === 's3') {
      if (!currentState.gallery_s3_bucket.trim()) {
        nextErrors.gallery_s3_bucket = 'S3 bucket is required when S3 storage is enabled.'
      }
      if (!currentState.gallery_s3_region.trim()) {
        nextErrors.gallery_s3_region = 'S3 region is required when S3 storage is enabled.'
      }
      if (!currentState.gallery_s3_access_key.trim()) {
        nextErrors.gallery_s3_access_key = 'S3 access key is required when S3 storage is enabled.'
      }
      if (currentState.clear_gallery_s3_secret_key && !currentState.gallery_s3_secret_key.trim()) {
        nextErrors.gallery_s3_secret_key = 'Enter a replacement secret key or switch the storage backend to Local before clearing it.'
      } else if (!data?.has_gallery_s3_secret_key && !currentState.gallery_s3_secret_key.trim()) {
        nextErrors.gallery_s3_secret_key = 'S3 secret key is required for first-time S3 setup.'
      }
    }

    return nextErrors
  }

  const handleSave = () => {
    if (!formState) {
      return
    }

    const nextErrors = validateForm(formState)
    setErrors(nextErrors)

    if (Object.values(nextErrors).some(Boolean)) {
      toast.error('Fix the highlighted fields before saving.')
      return
    }

    const galleryFileCount = Number.parseInt(formState.number_gallery_files, 10)
    const maximumFileSizeMb = Number.parseFloat(formState.maximum_file_size_mb)
    const workHoursPerDay = Number.parseFloat(formState.work_hours_per_day)

    const payload: JobManagementConfigurationUpdatePayload = {
      track_worker: formState.track_worker,
      push_notification: formState.push_notification,
      sms_notification: formState.sms_notification,
      email_notification: formState.email_notification,
      number_gallery_files: galleryFileCount,
      maximum_file_size: Math.round(maximumFileSizeMb * 1024 * 1024),
      work_hours_per_day: workHoursPerDay,
      per_day_worker_invoice_cutoff_time: formState.per_day_worker_invoice_cutoff_time,
      allow_worker_comments: formState.allow_worker_comments,
      require_otp_for_completion: formState.require_otp_for_completion,
      auto_assign_primary_worker: formState.auto_assign_primary_worker,
      allow_expense_uploads: formState.allow_expense_uploads,
      allow_multiple_assignments: formState.allow_multiple_assignments,
      enable_job_audit: formState.enable_job_audit,
      sender_email: formState.sender_email.trim() || null,
      sms_gateway: formState.sms_gateway.trim() || null,
      media_base_url: formState.media_base_url.trim() || null,
      gallery_storage_backend: formState.gallery_storage_backend,
      gallery_s3_bucket: formState.gallery_s3_bucket.trim() || null,
      gallery_s3_region: formState.gallery_s3_region.trim() || null,
      gallery_s3_access_key: formState.gallery_s3_access_key.trim() || null,
      clear_gallery_s3_secret_key: formState.clear_gallery_s3_secret_key,
    }

    if (formState.gallery_s3_secret_key.trim()) {
      payload.gallery_s3_secret_key = formState.gallery_s3_secret_key.trim()
    }

    updateMutation.mutate(payload)
  }

  if (error) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-destructive/20 bg-card">
        <div className="text-center">
          <p className="mb-1 text-sm text-destructive">{`Error loading ${labels.entityLower} configuration`}</p>
          <p className="text-xs text-muted-foreground">{getErrorMessage(error)}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3 pb-2">
      <Card className="rounded-xl border-border/80">
        <CardContent className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold tracking-tight text-foreground">
                {labels.configurationManager}
              </h1>
              <Badge variant="outline" className="rounded-md border-blue-200 bg-blue-50 px-1.5 py-0 text-[10px] text-blue-700">
                Service branch
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Notifications, workflow rules, media limits, and storage for this branch.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {data ? (
              <>
                <Badge variant="secondary" className="gap-1 px-2 py-0.5 text-[11px] font-normal">
                  <FileImage className="h-3 w-3" />
                  {`${data.number_gallery_files} files/${labels.entityLower}`}
                </Badge>
                <Badge variant="secondary" className="gap-1 px-2 py-0.5 text-[11px] font-normal">
                  <HardDrive className="h-3 w-3" />
                  {formatBytes(data.maximum_file_size)} max
                </Badge>
                <Badge variant="outline" className="px-2 py-0.5 text-[11px] font-normal uppercase">
                  {(data.gallery_storage_backend || 'local')}
                </Badge>
              </>
            ) : isLoading ? (
              <>
                <Skeleton className="h-6 w-24 rounded-md" />
                <Skeleton className="h-6 w-24 rounded-md" />
              </>
            ) : null}

            {isEditing ? (
              <>
                <Button type="button" variant="outline" size="sm" className="h-8" onClick={handleCancelEditing} disabled={updateMutation.isPending}>
                  Cancel
                </Button>
                <Button type="button" size="sm" className="h-8" onClick={handleSave} disabled={updateMutation.isPending}>
                  <Save className="mr-1.5 h-3.5 w-3.5" />
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button type="button" size="sm" className="h-8" onClick={handleStartEditing} disabled={isLoading || !formState}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card className="rounded-xl border-border/80">
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full rounded-lg" />
            ))}
          </CardContent>
        </Card>
      ) : data && formState ? (
        <Card className="rounded-xl border-border/80">
          <CardHeader className="px-4 py-3">
            <CardTitle className="text-sm font-medium">Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <Tabs defaultValue="notifications" className="gap-3">
              <TabsList className="grid h-8 w-full grid-cols-3 rounded-lg bg-muted/60 p-0.5">
                <TabsTrigger value="notifications" className="h-7 rounded-md text-xs">Notifications</TabsTrigger>
                <TabsTrigger value="workflow" className="h-7 rounded-md text-xs">Workflow</TabsTrigger>
                <TabsTrigger value="media" className="h-7 rounded-md text-xs">Media</TabsTrigger>
              </TabsList>

              <TabsContent value="notifications" className="mt-3 space-y-3">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {notificationItems.map((item) => (
                    <ToggleRow
                      key={item.key}
                      label={item.label}
                      checked={Boolean(formState[item.key as keyof ConfigurationFormState])}
                      onCheckedChange={(checked) => setBooleanField(item.key as keyof ConfigurationFormState, checked)}
                      disabled={!isEditing || updateMutation.isPending}
                      icon={item.icon}
                    />
                  ))}
                </div>
                <Separator />
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="sender_email" className="text-xs">Sender email</Label>
                    <Input
                      id="sender_email"
                      value={formState.sender_email}
                      onChange={(event) => setTextField('sender_email', event.target.value)}
                      disabled={!isEditing || updateMutation.isPending}
                      className={`h-8 text-sm ${errors.sender_email ? fieldErrorClassName : ''}`}
                    />
                    {errors.sender_email ? <p className="text-xs text-destructive">{errors.sender_email}</p> : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sms_gateway" className="text-xs">SMS gateway</Label>
                    <Input
                      id="sms_gateway"
                      value={formState.sms_gateway}
                      onChange={(event) => setTextField('sms_gateway', event.target.value)}
                      disabled={!isEditing || updateMutation.isPending}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="workflow" className="mt-3 space-y-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  {visibleWorkflowItems.map((item) => (
                    <ToggleRow
                      key={item.key}
                      label={item.label}
                      checked={Boolean(formState[item.key as keyof ConfigurationFormState])}
                      onCheckedChange={(checked) => setBooleanField(item.key as keyof ConfigurationFormState, checked)}
                      disabled={!isEditing || updateMutation.isPending}
                      icon={item.icon}
                    />
                  ))}
                </div>
                <Separator />
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="work_hours_per_day" className="text-xs">Hours per day</Label>
                    <div className="relative">
                      <Clock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="work_hours_per_day"
                        inputMode="decimal"
                        value={formState.work_hours_per_day}
                        onChange={(event) => setTextField('work_hours_per_day', event.target.value)}
                        disabled={!isEditing || updateMutation.isPending}
                        className={`h-8 pl-8 text-sm ${errors.work_hours_per_day ? fieldErrorClassName : ''}`}
                      />
                    </div>
                    {errors.work_hours_per_day ? <p className="text-xs text-destructive">{errors.work_hours_per_day}</p> : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="per_day_worker_invoice_cutoff_time" className="text-xs">Per-day invoice cutoff</Label>
                    <div className="relative">
                      <Clock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="per_day_worker_invoice_cutoff_time"
                        type="time"
                        value={formState.per_day_worker_invoice_cutoff_time}
                        onChange={(event) => setTextField('per_day_worker_invoice_cutoff_time', event.target.value)}
                        disabled={!isEditing || updateMutation.isPending}
                        className={`h-8 pl-8 text-sm ${errors.per_day_worker_invoice_cutoff_time ? fieldErrorClassName : ''}`}
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Generates per-day worker payout invoices from completed jobs after this branch-local time.
                    </p>
                    {errors.per_day_worker_invoice_cutoff_time ? <p className="text-xs text-destructive">{errors.per_day_worker_invoice_cutoff_time}</p> : null}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="media" className="mt-3 space-y-3">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {mediaItems.map((item) => (
                    <ToggleRow
                      key={item.key}
                      label={item.label}
                      checked={Boolean(formState[item.key as keyof ConfigurationFormState])}
                      onCheckedChange={(checked) => setBooleanField(item.key as keyof ConfigurationFormState, checked)}
                      disabled={!isEditing || updateMutation.isPending}
                      icon={item.icon}
                    />
                  ))}
                </div>
                <Separator />
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="number_gallery_files" className="text-xs">Files per job</Label>
                    <Input
                      id="number_gallery_files"
                      inputMode="numeric"
                      value={formState.number_gallery_files}
                      onChange={(event) => setTextField('number_gallery_files', event.target.value)}
                      disabled={!isEditing || updateMutation.isPending}
                      className={`h-8 text-sm ${errors.number_gallery_files ? fieldErrorClassName : ''}`}
                    />
                    {errors.number_gallery_files ? <p className="text-xs text-destructive">{errors.number_gallery_files}</p> : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="maximum_file_size_mb" className="text-xs">Max file size (MB)</Label>
                    <Input
                      id="maximum_file_size_mb"
                      inputMode="decimal"
                      value={formState.maximum_file_size_mb}
                      onChange={(event) => setTextField('maximum_file_size_mb', event.target.value)}
                      disabled={!isEditing || updateMutation.isPending}
                      className={`h-8 text-sm ${errors.maximum_file_size_mb ? fieldErrorClassName : ''}`}
                    />
                    {errors.maximum_file_size_mb ? <p className="text-xs text-destructive">{errors.maximum_file_size_mb}</p> : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="gallery_storage_backend" className="text-xs">Storage backend</Label>
                    <select
                      id="gallery_storage_backend"
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2.5 text-sm"
                      value={formState.gallery_storage_backend}
                      onChange={(event) => setTextField('gallery_storage_backend', event.target.value as 'local' | 's3')}
                      disabled={!isEditing || updateMutation.isPending}
                    >
                      <option value="local">Local</option>
                      <option value="s3">S3</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="media_base_url" className="text-xs">Media base URL</Label>
                  <Input
                    id="media_base_url"
                    value={formState.media_base_url}
                    onChange={(event) => setTextField('media_base_url', event.target.value)}
                    disabled={!isEditing || updateMutation.isPending}
                    className={`h-8 text-sm ${errors.media_base_url ? fieldErrorClassName : ''}`}
                  />
                  {errors.media_base_url ? <p className="text-xs text-destructive">{errors.media_base_url}</p> : null}
                </div>

                {data.has_gallery_s3_secret_key ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/10 px-3 py-2">
                    <p className="text-xs text-muted-foreground">Stored S3 secret on file</p>
                    <Button
                      type="button"
                      variant={formState.clear_gallery_s3_secret_key ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setBooleanField('clear_gallery_s3_secret_key', !formState.clear_gallery_s3_secret_key)}
                      disabled={!isEditing || updateMutation.isPending}
                    >
                      {formState.clear_gallery_s3_secret_key ? 'Keep secret' : 'Clear secret'}
                    </Button>
                  </div>
                ) : null}

                {formState.gallery_storage_backend === 's3' ? (
                  <div className="space-y-3 rounded-lg border border-border/60 bg-muted/10 p-3">
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Cloud className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                      <p>Secrets are not returned to the browser. Leave blank to keep the stored secret.</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="gallery_s3_bucket" className="text-xs">S3 bucket</Label>
                        <Input
                          id="gallery_s3_bucket"
                          value={formState.gallery_s3_bucket}
                          onChange={(event) => setTextField('gallery_s3_bucket', event.target.value)}
                          disabled={!isEditing || updateMutation.isPending}
                          className={`h-8 text-sm ${errors.gallery_s3_bucket ? fieldErrorClassName : ''}`}
                        />
                        {errors.gallery_s3_bucket ? <p className="text-xs text-destructive">{errors.gallery_s3_bucket}</p> : null}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="gallery_s3_region" className="text-xs">S3 region</Label>
                        <Input
                          id="gallery_s3_region"
                          value={formState.gallery_s3_region}
                          onChange={(event) => setTextField('gallery_s3_region', event.target.value)}
                          disabled={!isEditing || updateMutation.isPending}
                          className={`h-8 text-sm ${errors.gallery_s3_region ? fieldErrorClassName : ''}`}
                        />
                        {errors.gallery_s3_region ? <p className="text-xs text-destructive">{errors.gallery_s3_region}</p> : null}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="gallery_s3_access_key" className="text-xs">S3 access key</Label>
                        <Input
                          id="gallery_s3_access_key"
                          value={formState.gallery_s3_access_key}
                          onChange={(event) => setTextField('gallery_s3_access_key', event.target.value)}
                          disabled={!isEditing || updateMutation.isPending}
                          className={`h-8 text-sm ${errors.gallery_s3_access_key ? fieldErrorClassName : ''}`}
                        />
                        {errors.gallery_s3_access_key ? <p className="text-xs text-destructive">{errors.gallery_s3_access_key}</p> : null}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="gallery_s3_secret_key" className="text-xs">S3 secret key</Label>
                        <Input
                          id="gallery_s3_secret_key"
                          type="password"
                          placeholder={data.has_gallery_s3_secret_key ? 'Stored — enter to rotate' : 'Enter secret key'}
                          value={formState.gallery_s3_secret_key}
                          onChange={(event) => setTextField('gallery_s3_secret_key', event.target.value)}
                          disabled={!isEditing || updateMutation.isPending}
                          className={`h-8 text-sm ${errors.gallery_s3_secret_key ? fieldErrorClassName : ''}`}
                        />
                        {errors.gallery_s3_secret_key ? <p className="text-xs text-destructive">{errors.gallery_s3_secret_key}</p> : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
